import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileText, Link2, Plus, ReceiptText, Upload } from "lucide-react";
import { type FormEvent, useState } from "react";
import { api, jsonBody, queryString } from "../../api/client";
import type { Booking, EventRecord, Invoice, ListResponse, Payment, Quote } from "../../api/types";
import { useTenantCurrency } from "../../app/tenant";
import { useVenueFilter, VenueFilterSelect } from "../../app/venueFilter";
import { useWorkspaceLens } from "../../app/workspaceLens";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows } from "../../auth/permissions";
import { useSensitiveAction } from "../../auth/SensitiveActionGate";
import { formatDate, formatMoney, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Metric,
  Modal,
  Notice,
  PageHeader,
  ReasonDialog,
  StatusPill,
} from "../../ui/components";
import { FinanceSkeleton, SkeletonCard, SkeletonFilters, SkeletonTable } from "../../ui/skeletons";
import { ContactPicker } from "../contacts/ContactPicker";
import { FinanceOperations } from "./FinanceOperations";
import { InvoiceFinanceControls } from "./InvoiceFinanceControls";

interface PaymentLinkResult {
  id: string;
  url: string;
  amountMinor: number;
  currency: string;
  status: string;
  expiresAt: string;
}

export function FinancePage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const membership = auth.session?.selectedMembership;
  const lens = useWorkspaceLens();
  const sensitive = useSensitiveAction({
    setupTitle: "Protect refunds with two-step verification",
    lockedTitle: "Confirm your identity before a refund",
    lockedDescription:
      "Refunds return customer money and create a permanent record. Enter a fresh code before continuing.",
  });
  const canRecordPayments = membershipAllows(membership, "payments.record");
  const canRefund = membershipAllows(membership, "refunds.manage");
  const canManagePeriods = canRefund && Boolean(membership?.allVenues);
  // Finance is one of the two pages (with Analytics) that may widen beyond the venue picked in
  // the top bar, because money questions are often asked across the whole organization. The
  // filter starts on the top-bar venue and can open up to every venue this person may see.
  const filter = useVenueFilter();
  const venueId = filter.venueId;
  // The lens. The event workspace keeps the client's own money: invoices raised for its events
  // and sponsorship payment requests. The booking workspace keeps what hirers owe, and the quotes
  // that lead there. One ledger, asked one question at a time.
  const workspace = lens.workspace;
  const currency = useTenantCurrency();
  const [tab, setTab] = useState<"invoices" | "quotes">("invoices");
  const activeTab = lens.isBookings ? tab : "invoices";
  const [createType, setCreateType] = useState<"invoice" | "quote" | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [offlineOpen, setOfflineOpen] = useState(false);
  const [error, setError] = useState<unknown>();
  const [working, setWorking] = useState(false);
  const [paymentLink, setPaymentLink] = useState<PaymentLinkResult | null>(null);
  const [refundTarget, setRefundTarget] = useState<Payment | null>(null);
  const [refundKey, setRefundKey] = useState("");
  const [quoteTransition, setQuoteTransition] = useState<{
    quote: Quote;
    status: "issued" | "accepted" | "declined";
  } | null>(null);
  const invoices = useQuery({
    queryKey: ["invoices", workspace, venueId],
    queryFn: () =>
      api<ListResponse<Invoice>>(
        `/api/v1/invoices${queryString({ workspace, venueId, limit: 100, offset: 0 })}`,
      ),
    enabled: filter.ready,
  });
  const quotes = useQuery({
    queryKey: ["quotes", venueId],
    queryFn: () =>
      api<ListResponse<Quote>>(`/api/v1/quotes${queryString({ venueId, limit: 100, offset: 0 })}`),
    enabled: filter.ready && lens.isBookings,
  });
  // The pickers behind a new document: hires on the booking side, the client's own events on the
  // events side. Either is fetched only once somebody opens the form, and only for the venues in
  // view, so a document is never raised against something the page would not show.
  const documentWindowMilliseconds = 180 * 24 * 60 * 60 * 1000;
  const bookings = useQuery({
    queryKey: ["bookings", "finance-picker", venueId],
    queryFn: () => {
      const now = Date.now();
      const from = new Date(now - documentWindowMilliseconds).toISOString();
      const to = new Date(now + documentWindowMilliseconds).toISOString();
      return api<ListResponse<Booking>>(`/api/v1/bookings${queryString({ from, to, venueId })}`);
    },
    enabled: createType !== null && lens.isBookings,
  });
  const events = useQuery({
    queryKey: ["events", "finance-picker", venueId],
    queryFn: () => {
      const now = Date.now();
      const from = new Date(now - documentWindowMilliseconds).toISOString();
      const to = new Date(now + documentWindowMilliseconds).toISOString();
      return api<ListResponse<EventRecord>>(`/api/v1/events${queryString({ from, to, venueId })}`);
    },
    enabled: createType !== null && lens.isEvents,
  });
  const invoice = useQuery({
    queryKey: ["invoices", selectedInvoiceId],
    queryFn: () => api<Invoice>(`/api/v1/invoices/${selectedInvoiceId}`),
    enabled: Boolean(selectedInvoiceId),
  });
  const payments = useQuery({
    queryKey: ["invoices", selectedInvoiceId, "payments"],
    queryFn: () => api<ListResponse<Payment>>(`/api/v1/invoices/${selectedInvoiceId}/payments`),
    enabled: Boolean(selectedInvoiceId) && canRecordPayments,
  });

  if (!filter.ready || invoices.isLoading || (lens.isBookings && quotes.isLoading)) {
    return <FinanceSkeleton />;
  }
  if (invoices.error || quotes.error) return <ErrorPanel error={invoices.error ?? quotes.error} />;
  const invoiceItems = invoices.data?.items ?? [];
  const outstanding = invoiceItems.reduce((sum, item) => sum + item.balanceMinor, 0);
  const invoiced = invoiceItems.reduce((sum, item) => sum + item.totalMinor, 0);
  const overdue = invoiceItems
    .filter((item) => item.dueAt && new Date(item.dueAt) < new Date() && item.balanceMinor > 0)
    .reduce((sum, item) => sum + item.balanceMinor, 0);

  function moneyMinor(value: FormDataEntryValue | null): number {
    return Math.round(Number(value ?? 0) * 100);
  }

  async function createDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!createType) return;
    const data = new FormData(event.currentTarget);
    const customerId = String(data.get("customerId"));
    // What the document bills: one of the client's own events on the events side, a hire on the
    // booking side. Quotes only exist for hires, so a quote always carries a booking.
    const owner = lens.isEvents
      ? { eventId: String(data.get("eventId")) }
      : { bookingId: String(data.get("bookingId")) };
    const line = {
      description: String(data.get("description")),
      quantityMilli: Math.round(Number(data.get("quantity")) * 1000),
      unit: String(data.get("unit")),
      unitAmountMinor: moneyMinor(data.get("unitAmount")),
      discountMinor: moneyMinor(data.get("lineDiscount")),
      taxRateBasisPoints: Math.round(Number(data.get("taxRate")) * 100),
    };
    const common = {
      ...owner,
      customerId,
      // Quotes are validated against a real currency code; invoices treat this as advisory
      // and stamp the tenant base currency on the server either way.
      currency,
      discountMinor: moneyMinor(data.get("documentDiscount")),
      lines: [line],
    };
    const payload =
      createType === "invoice"
        ? {
            ...common,
            dueAt: data.get("dueAt") ? new Date(String(data.get("dueAt"))).toISOString() : null,
          }
        : {
            ...common,
            expiresAt: data.get("expiresAt")
              ? new Date(String(data.get("expiresAt"))).toISOString()
              : null,
          };
    setWorking(true);
    setError(undefined);
    try {
      await api(createType === "invoice" ? "/api/v1/invoices" : "/api/v1/quotes", {
        method: "POST",
        body: jsonBody(payload),
      });
      await queryClient.invalidateQueries({
        queryKey: [createType === "invoice" ? "invoices" : "quotes"],
      });
      setCreateType(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function issueInvoice() {
    if (!invoice.data) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/invoices/${invoice.data.id}/issue`, {
        method: "POST",
        body: jsonBody({ version: invoice.data.version }),
      });
      await Promise.all([invoice.refetch(), invoices.refetch()]);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function createLink() {
    if (!invoice.data) return;
    setWorking(true);
    setError(undefined);
    try {
      const result = await api<PaymentLinkResult>(
        `/api/v1/invoices/${invoice.data.id}/payment-links`,
        {
          method: "POST",
          body: jsonBody({ amountMinor: invoice.data.balanceMinor, scenario: "success" }),
        },
      );
      setPaymentLink(result);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function recordOffline(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!invoice.data) return;
    const body = new FormData(event.currentTarget);
    body.set("amountMinor", String(moneyMinor(body.get("amount"))));
    body.delete("amount");
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/invoices/${invoice.data.id}/offline-payments`, { method: "POST", body });
      await Promise.all([invoice.refetch(), payments.refetch(), invoices.refetch()]);
      setOfflineOpen(false);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function refund(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!refundTarget || !refundKey) return;
    const data = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/payments/${refundTarget.id}/refunds`, {
        method: "POST",
        headers: { "Idempotency-Key": refundKey },
        body: jsonBody({
          amountMinor: moneyMinor(data.get("amount")),
          reason: String(data.get("reason") ?? "").trim(),
        }),
      });
      await Promise.all([payments.refetch(), invoice.refetch(), invoices.refetch()]);
      setRefundTarget(null);
      setRefundKey("");
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function transitionQuote(reason: string) {
    if (!quoteTransition) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/quotes/${quoteTransition.quote.id}/transition`, {
        method: "POST",
        body: jsonBody({ status: quoteTransition.status, reason }),
      });
      await quotes.refetch();
      setQuoteTransition(null);
      setSelectedQuote(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  function requestQuoteTransition(status: "issued" | "accepted" | "declined") {
    if (!selectedQuote) return;
    setError(undefined);
    setQuoteTransition({ quote: selectedQuote, status });
  }

  if (createType) {
    return (
      <section className="editor-page">
        <PageHeader
          title={`New ${createType}`}
          description="Create the first itemized version in the full finance workspace."
          actions={
            <button className="button secondary" type="button" onClick={() => setCreateType(null)}>
              Back to finance
            </button>
          }
        />
        {error ? <ErrorPanel error={error} /> : null}
        {(lens.isEvents ? events.isLoading : bookings.isLoading) ? (
          <SkeletonFilters count={8} />
        ) : (lens.isEvents ? events.error : bookings.error) ? (
          <ErrorPanel error={lens.isEvents ? events.error : bookings.error} />
        ) : (
          <Card>
            <form className="form-grid" onSubmit={createDocument}>
              <Field
                label="Customer"
                hint="Search by name, organization or email. Create them here if they are new."
              >
                <ContactPicker name="customerId" required />
              </Field>
              {lens.isEvents ? (
                <Field
                  label="Event"
                  hint="The invoice is anchored to this event and shows on its money card."
                >
                  <select name="eventId" required defaultValue="">
                    <option value="" disabled>
                      Select event
                    </option>
                    {events.data?.items.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.reference} · {event.title}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : (
                <Field label="Booking">
                  <select name="bookingId" required defaultValue="">
                    <option value="" disabled>
                      Select booking
                    </option>
                    {bookings.data?.items.map((booking) => (
                      <option key={booking.id} value={booking.id}>
                        {booking.reference} · {booking.title}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label="Line description">
                <input
                  name="description"
                  required
                  defaultValue={lens.isEvents ? "Event fee" : "Venue rental"}
                />
              </Field>
              <Field label="Quantity">
                <input
                  name="quantity"
                  type="number"
                  min={0.001}
                  step={0.001}
                  defaultValue={1}
                  required
                />
              </Field>
              <Field label="Unit">
                <input name="unit" defaultValue={lens.isEvents ? "each" : "booking"} required />
              </Field>
              <Field label={`Unit amount (${currency})`}>
                <input name="unitAmount" type="number" min={0} step={0.01} required />
              </Field>
              <Field label={`Line discount (${currency})`}>
                <input name="lineDiscount" type="number" min={0} step={0.01} defaultValue={0} />
              </Field>
              <Field label="Tax rate (%)">
                <input
                  name="taxRate"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  defaultValue={0}
                />
              </Field>
              <Field label={`Document discount (${currency})`}>
                <input name="documentDiscount" type="number" min={0} step={0.01} defaultValue={0} />
              </Field>
              {createType === "invoice" ? (
                <Field label="Due at">
                  <input name="dueAt" type="datetime-local" />
                </Field>
              ) : (
                <Field label="Expires at">
                  <input name="expiresAt" type="datetime-local" />
                </Field>
              )}
              <div className="full">
                <FormActions
                  submitting={working}
                  submitLabel={`Create ${createType}`}
                  onCancel={() => setCreateType(null)}
                />
              </div>
            </form>
          </Card>
        )}
      </section>
    );
  }

  if (selectedInvoiceId) {
    if (offlineOpen) {
      return (
        <section className="editor-page">
          <PageHeader
            title="Record offline payment"
            description="A photograph or scan is mandatory and retained with the payment record."
            actions={
              <button
                className="button secondary"
                type="button"
                onClick={() => setOfflineOpen(false)}
              >
                Back to invoice
              </button>
            }
          />
          {error ? <ErrorPanel error={error} /> : null}
          {invoice.isLoading ? (
            <SkeletonFilters count={6} />
          ) : invoice.error ? (
            <ErrorPanel error={invoice.error} />
          ) : invoice.data ? (
            <Card>
              <form className="form-grid" onSubmit={recordOffline}>
                <Field label="Method">
                  <select name="method" defaultValue="cash">
                    <option value="cash">Cash</option>
                    <option value="check">Check</option>
                    <option value="bank_transfer">Bank transfer</option>
                    <option value="card_terminal">Card terminal</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field label={`Amount (${invoice.data.currency})`}>
                  <input
                    name="amount"
                    type="number"
                    min={0.01}
                    step={0.01}
                    max={invoice.data.balanceMinor / 100}
                    required
                  />
                </Field>
                <Field label="Evidence type">
                  <select name="evidenceType" defaultValue="receipt">
                    <option value="receipt">Receipt photograph</option>
                    <option value="check">Check photograph</option>
                    <option value="bank_confirmation">Bank confirmation</option>
                    <option value="terminal_slip">Terminal receipt</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field
                  label="Photograph or scan"
                  hint="Use JPEG, PNG, WebP, or PDF within the configured upload limit."
                >
                  <input
                    name="evidence"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    capture="environment"
                    required
                  />
                </Field>
                <Field label="Notes">
                  <textarea name="notes" required minLength={3} maxLength={2000} />
                </Field>
                <div className="full">
                  <FormActions
                    submitting={working}
                    submitLabel="Record payment"
                    onCancel={() => setOfflineOpen(false)}
                  />
                </div>
              </form>
            </Card>
          ) : null}
        </section>
      );
    }

    return (
      <section className="editor-page">
        <PageHeader
          title={invoice.data?.number || "Draft invoice"}
          description="Invoice, payment, evidence, and accounting controls."
          actions={
            <button
              className="button secondary"
              type="button"
              onClick={() => {
                setSelectedInvoiceId(null);
                setPaymentLink(null);
                setRefundTarget(null);
              }}
            >
              Back to finance
            </button>
          }
        />
        {invoice.isLoading ? (
          <div className="stack">
            <SkeletonCard />
            <SkeletonTable columns={5} rows={4} />
          </div>
        ) : invoice.error ? (
          <ErrorPanel error={invoice.error} />
        ) : invoice.data ? (
          <div className="stack">
            {error ? <ErrorPanel error={error} /> : null}
            <Card>
              <CardHeader
                title={invoice.data.customerName}
                actions={
                  <div className="inline">
                    <StatusPill value={invoice.data.status} />
                    <a
                      className="button secondary small"
                      href={`/api/v1/invoices/${invoice.data.id}/pdf`}
                      download
                    >
                      <Download size={16} /> PDF
                    </a>
                  </div>
                }
              />
              <dl className="definition-list">
                <dt>Total</dt>
                <dd>{formatMoney(invoice.data.totalMinor, invoice.data.currency)}</dd>
                <dt>Balance</dt>
                <dd>{formatMoney(invoice.data.balanceMinor, invoice.data.currency)}</dd>
                <dt>Due</dt>
                <dd>{formatDate(invoice.data.dueAt)}</dd>
                <dt>For</dt>
                <dd>
                  {invoice.data.bookingReference ||
                    invoice.data.eventReference ||
                    "A sponsorship payment request"}
                </dd>
              </dl>
            </Card>
            <DataTable
              caption="Invoice lines"
              items={invoice.data.lines ?? []}
              getKey={(line) => line.id}
              columns={[
                { label: "Description", render: (line) => line.description },
                { label: "Quantity", render: (line) => line.quantityMilli / 1000 },
                {
                  label: "Net",
                  className: "numeric",
                  render: (line) => formatMoney(line.netMinor, invoice.data?.currency),
                },
                {
                  label: "Tax",
                  className: "numeric",
                  render: (line) => formatMoney(line.taxMinor, invoice.data?.currency),
                },
                {
                  label: "Gross",
                  className: "numeric",
                  render: (line) => formatMoney(line.grossMinor, invoice.data?.currency),
                },
              ]}
            />
            <div className="inline">
              {invoice.data.status === "draft" ? (
                <button
                  className="button primary"
                  type="button"
                  disabled={working}
                  onClick={issueInvoice}
                >
                  Issue invoice
                </button>
              ) : null}
              {canRecordPayments &&
              invoice.data.balanceMinor > 0 &&
              invoice.data.status !== "draft" ? (
                <>
                  <button
                    className="button primary"
                    type="button"
                    disabled={working}
                    onClick={createLink}
                  >
                    <Link2 size={17} /> Create payment link
                  </button>
                  <button
                    className="button secondary"
                    type="button"
                    disabled={working}
                    onClick={() => {
                      setError(undefined);
                      setOfflineOpen(true);
                    }}
                  >
                    <Upload size={17} /> Record offline payment
                  </button>
                </>
              ) : null}
            </div>
            {paymentLink ? (
              <Notice tone="success" title="Payment link ready">
                <a href={paymentLink.url} target="_blank" rel="noreferrer">
                  Open the secure payment page
                </a>
              </Notice>
            ) : null}
            {canRecordPayments ? (
              <Card>
                <CardHeader title="Payments" />
                {payments.error ? (
                  <ErrorPanel error={payments.error} />
                ) : payments.isLoading ? (
                  <SkeletonTable bare columns={5} rows={3} />
                ) : payments.data?.items.length ? (
                  <DataTable
                    caption="Invoice payments"
                    items={payments.data.items}
                    getKey={(payment) => payment.id}
                    columns={[
                      { label: "Received", render: (payment) => formatDate(payment.receivedAt) },
                      { label: "Method", render: (payment) => titleCase(payment.method) },
                      {
                        label: "Amount",
                        className: "numeric",
                        render: (payment) => formatMoney(payment.amountMinor, payment.currency),
                      },
                      {
                        label: "Status",
                        render: (payment) => <StatusPill value={payment.status} />,
                      },
                      {
                        label: "Action",
                        render: (payment) =>
                          canRefund && payment.status === "succeeded" ? (
                            <button
                              className="button secondary small"
                              type="button"
                              disabled={working || !sensitive.unlocked}
                              onClick={() => {
                                setError(undefined);
                                setRefundKey(crypto.randomUUID());
                                setRefundTarget(payment);
                              }}
                            >
                              Refund
                            </button>
                          ) : (
                            "Not available"
                          ),
                      },
                    ]}
                  />
                ) : (
                  <EmptyState
                    title="No payments"
                    description="Create a payment link or record an evidence-backed offline payment."
                  />
                )}
              </Card>
            ) : null}
            <InvoiceFinanceControls
              invoice={invoice.data}
              payments={payments.data?.items ?? []}
              canRecordPayments={canRecordPayments}
              canManageFinance={canRefund}
              refresh={async () => {
                await Promise.all([payments.refetch(), invoice.refetch(), invoices.refetch()]);
              }}
            />
            <Modal
              open={Boolean(refundTarget)}
              onClose={() => {
                setRefundTarget(null);
                setRefundKey("");
              }}
              title="Refund payment"
              description={
                refundTarget
                  ? `Refund up to ${formatMoney(refundTarget.amountMinor, refundTarget.currency)}. This action is audited.`
                  : undefined
              }
            >
              {sensitive.view}
              {error ? <ErrorPanel error={error} /> : null}
              {refundTarget ? (
                <form className="form-stack" onSubmit={refund}>
                  <Field label={`Amount (${refundTarget.currency})`}>
                    <input
                      data-autofocus
                      name="amount"
                      type="number"
                      min={0.01}
                      max={refundTarget.amountMinor / 100}
                      step={0.01}
                      defaultValue={(refundTarget.amountMinor / 100).toFixed(2)}
                      required
                    />
                  </Field>
                  <Field
                    label="Reason"
                    hint="Explain why this refund is required for the audit record."
                  >
                    <textarea name="reason" required minLength={5} maxLength={1000} />
                  </Field>
                  <FormActions
                    submitting={working}
                    disabled={!sensitive.unlocked}
                    submitLabel="Issue refund"
                    tone="danger"
                    onCancel={() => {
                      setRefundTarget(null);
                      setRefundKey("");
                    }}
                  />
                </form>
              ) : null}
            </Modal>
          </div>
        ) : null}
      </section>
    );
  }

  if (selectedQuote) {
    return (
      <section className="editor-page">
        <PageHeader
          title={selectedQuote.number}
          description="Quote summary and lifecycle controls."
          actions={
            <button
              className="button secondary"
              type="button"
              onClick={() => setSelectedQuote(null)}
            >
              Back to finance
            </button>
          }
        />
        {error ? <ErrorPanel error={error} /> : null}
        <div className="stack">
          <Card>
            <CardHeader
              title="Quote summary"
              actions={
                <div className="inline">
                  <StatusPill value={selectedQuote.status} />
                  <a
                    className="button secondary small"
                    href={`/api/v1/quotes/${selectedQuote.id}/pdf`}
                    download
                  >
                    <Download size={16} /> PDF
                  </a>
                </div>
              }
            />
            <dl className="definition-list">
              <dt>Total</dt>
              <dd>{formatMoney(selectedQuote.totalMinor, selectedQuote.currency)}</dd>
              <dt>Expires</dt>
              <dd>{formatDate(selectedQuote.expiresAt)}</dd>
              <dt>Version</dt>
              <dd>{selectedQuote.currentVersion}</dd>
            </dl>
          </Card>
          <div className="inline">
            {selectedQuote.status === "draft" ? (
              <button
                className="button primary"
                type="button"
                disabled={working}
                onClick={() => requestQuoteTransition("issued")}
              >
                Issue quote
              </button>
            ) : null}
            {selectedQuote.status === "issued" ? (
              <>
                <button
                  className="button primary"
                  type="button"
                  disabled={working}
                  onClick={() => requestQuoteTransition("accepted")}
                >
                  Mark accepted
                </button>
                <button
                  className="button secondary"
                  type="button"
                  disabled={working}
                  onClick={() => requestQuoteTransition("declined")}
                >
                  Mark declined
                </button>
              </>
            ) : null}
          </div>
        </div>
        <ReasonDialog
          open={quoteTransition !== null}
          title={quoteTransition ? `${titleCase(quoteTransition.status)} quote` : "Update quote"}
          description={
            quoteTransition
              ? `Record why ${quoteTransition.quote.number} is being marked ${titleCase(quoteTransition.status)}.`
              : undefined
          }
          submitLabel={
            quoteTransition ? `Mark ${titleCase(quoteTransition.status)}` : "Update quote"
          }
          submitting={working}
          tone={quoteTransition?.status === "declined" ? "danger" : "primary"}
          onClose={() => setQuoteTransition(null)}
          onSubmit={transitionQuote}
        />
      </section>
    );
  }

  return (
    <>
      <PageHeader
        title="Finance"
        description={
          lens.isEvents
            ? "Invoices for your own events and sponsorship payment requests, with payments, offline evidence, reconciliation, credits, refunds, and accounting controls."
            : "Quotes, invoices, payment schedules, offline evidence, reconciliation, credits, disputes, refunds, and accounting controls."
        }
        actions={
          <button
            className="button primary"
            type="button"
            onClick={() => setCreateType(activeTab === "invoices" ? "invoice" : "quote")}
          >
            <Plus size={17} /> New {activeTab === "invoices" ? "invoice" : "quote"}
          </button>
        }
      />

      <div className="metrics-grid">
        <Metric label="Invoiced" value={formatMoney(invoiced, currency)} />
        <Metric label="Outstanding" value={formatMoney(outstanding, currency)} />
        <Metric label="Overdue" value={formatMoney(overdue, currency)} />
        <Metric
          label="Documents"
          value={invoiceItems.length + (lens.isBookings ? (quotes.data?.items.length ?? 0) : 0)}
        />
      </div>

      {filter.canWiden ? (
        <Card>
          <div className="filters">
            <VenueFilterSelect filter={filter} />
          </div>
        </Card>
      ) : null}

      {lens.isBookings ? (
        <div className="inline" role="tablist" aria-label="Finance views">
          <button
            className={`button ${activeTab === "invoices" ? "primary" : "secondary"}`}
            type="button"
            role="tab"
            aria-selected={activeTab === "invoices"}
            onClick={() => setTab("invoices")}
          >
            <ReceiptText size={17} /> Invoices
          </button>
          <button
            className={`button ${activeTab === "quotes" ? "primary" : "secondary"}`}
            type="button"
            role="tab"
            aria-selected={activeTab === "quotes"}
            onClick={() => setTab("quotes")}
          >
            <FileText size={17} /> Quotes
          </button>
        </div>
      ) : null}

      <Card>
        {activeTab === "invoices" ? (
          invoiceItems.length ? (
            <DataTable
              caption="Invoices"
              items={invoiceItems}
              getKey={(item) => item.id}
              columns={[
                {
                  label: "Invoice",
                  render: (item) => (
                    <button
                      className="button ghost small"
                      type="button"
                      onClick={() => setSelectedInvoiceId(item.id)}
                    >
                      {item.number || "Draft"}
                    </button>
                  ),
                },
                { label: "Customer", render: (item) => item.customerName },
                {
                  // An invoice bills a hire, one of the client's own events, or a sponsorship
                  // pledge. Naming which keeps one mixed list legible at a glance.
                  label: "For",
                  render: (item) => item.bookingReference || item.eventReference || "Sponsorship",
                },
                {
                  label: "Total",
                  className: "numeric",
                  render: (item) => formatMoney(item.totalMinor, item.currency),
                },
                {
                  label: "Balance",
                  className: "numeric",
                  render: (item) => formatMoney(item.balanceMinor, item.currency),
                },
                { label: "Due", render: (item) => formatDate(item.dueAt) },
                { label: "Status", render: (item) => <StatusPill value={item.status} /> },
              ]}
            />
          ) : (
            <EmptyState
              title="No invoices"
              description={
                lens.isEvents
                  ? "Create a draft invoice for one of your events and its customer."
                  : "Create a draft invoice from a booking and customer."
              }
            />
          )
        ) : quotes.data?.items.length ? (
          <DataTable
            caption="Quotes"
            items={quotes.data.items}
            getKey={(item) => item.id}
            columns={[
              {
                label: "Quote",
                render: (item) => (
                  <button
                    className="button ghost small"
                    type="button"
                    onClick={() => setSelectedQuote(item)}
                  >
                    {item.number}
                  </button>
                ),
              },
              {
                label: "Total",
                className: "numeric",
                render: (item) => formatMoney(item.totalMinor, item.currency),
              },
              { label: "Expires", render: (item) => formatDate(item.expiresAt) },
              { label: "Version", render: (item) => item.currentVersion },
              { label: "Status", render: (item) => <StatusPill value={item.status} /> },
            ]}
          />
        ) : (
          <EmptyState title="No quotes" description="Create an itemized quote for a booking." />
        )}
      </Card>

      <FinanceOperations
        canManageFinance={canRefund}
        canRecordPayments={canRecordPayments}
        canManagePeriods={canManagePeriods}
      />
    </>
  );
}
